import React from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { History, Download, ArrowUpRight, ArrowDownRight } from "@/components/icons";
import { format } from "date-fns";
import type { CreditTransaction } from "@/types/credits";

interface TransactionHistoryProps {
  transactions: CreditTransaction[];
}

export const TransactionHistory: React.FC<TransactionHistoryProps> = React.memo(({
  transactions
}) => {
  const formatDate = (dateString: string) => {
    return format(new Date(dateString), "MMM dd, yyyy 'at' h:mm a");
  };

  return (
    <Card className="glassmorphism shadow-lg">
      <CardHeader className="pb-6">
        <div className="flex items-center gap-3 mb-2">
          <div className="p-2 bg-gradient-to-br from-primary/20 to-primary/10 rounded-lg">
            <History className="h-6 w-6 text-primary" />
          </div>
          <CardTitle className="text-2xl font-bold text-foreground">
            Transaction History
          </CardTitle>
        </div>
        <p className="text-base text-muted-foreground">
          View your recent credit transactions
        </p>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {transactions.map((transaction, index) => (
            <div key={transaction.id} className="group">
              <div className="flex items-center justify-between p-4 rounded-lg hover:bg-muted/50 transition-all duration-200">
                <div className="flex items-center gap-4">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center shadow-lg transition-all duration-200 group-hover:scale-105 ${
                    transaction.type === 'purchase' 
                      ? 'bg-gradient-to-br from-green-500 to-green-600 text-white' 
                      : 'bg-gradient-to-br from-red-500 to-red-600 text-white'
                  }`}>
                    {transaction.type === 'purchase' ? (
                      <ArrowUpRight className="h-5 w-5" />
                    ) : (
                      <ArrowDownRight className="h-5 w-5" />
                    )}
                  </div>
                  <div>
                    <div className="font-semibold text-base text-foreground">{transaction.description}</div>
                    <div className="text-sm text-muted-foreground mt-1">
                      {formatDate(transaction.timestamp)}
                    </div>
                    {transaction.channelId && (
                      <Badge variant="outline" className="mt-2 text-xs">
                        {transaction.channelId}
                      </Badge>
                    )}
                  </div>
                </div>
                <div className="text-right">
                  <div className={`font-bold text-lg ${
                    transaction.type === 'purchase' ? 'text-green-600' : 'text-red-600'
                  }`}>
                    {transaction.type === 'purchase' ? '+' : ''}{transaction.amount.toLocaleString()}
                  </div>
                  <div className="text-sm text-muted-foreground">
                    {transaction.type === 'purchase' ? 'Added' : 'Used'}
                  </div>
                </div>
              </div>
              {index < transactions.length - 1 && (
                <Separator className="my-2 opacity-50" />
              )}
            </div>
          ))}
        </div>

        <div className="mt-8 flex justify-center">
          <Button variant="outline" className="flex items-center gap-2 h-12 px-6 text-base font-semibold border-2 hover:bg-primary hover:text-primary-foreground hover:border-primary transition-all duration-200">
            <Download className="h-5 w-5" />
            Download Statement
          </Button>
        </div>
      </CardContent>
    </Card>
  );
});